<template>
  <div>
    <div v-if="details">
      <b-breadcrumb size="is-large" separator="has-succeeds-separator" aria-label="breadcrumbs">
        <b-breadcrumb-item tag="a" @click.prevent="goToList()">
          {{ $t('firmwares.title') }}
        </b-breadcrumb-item>
        <b-breadcrumb-item active>
          {{ shortSha(details.sha256) }}
        </b-breadcrumb-item>
      </b-breadcrumb>

      <div class="box">
        <h3 class="title is-5">{{ $t('devices.details') }}</h3>
        <table class="table is-fullwidth">
          <tbody>
            <tr>
              <td><strong>SHA256:</strong></td>
              <td>
                <div class="is-flex is-align-items-center is-family-monospace">
                  {{ details.sha256 }}
                  <CopyToClipboard :value="details.sha256" title="Copy" class="ml-1" />
                </div>
              </td>
            </tr>
            <tr>
              <td><strong>{{ $t('firmwares.product') }}:</strong></td>
              <td>{{ details.productId || '—' }}</td>
            </tr>
            <tr>
              <td><strong>{{ $t('projects.title').slice(0,-1) }}:</strong></td>
              <td>
                <template v-if="(details as any).projectId">
                  <router-link :to="{ name: 'project-edit', params: { id: (details as any).projectId } }">
                    {{ (details as any).projectName || String((details as any).projectId) }}
                  </router-link>
                </template>
                <template v-else>
                  {{ (details as any).projectName || '—' }}
                </template>
              </td>
            </tr>
            <tr>
              <td><strong>Size:</strong></td>
              <td>{{ formatSize(details.size) }}</td>
            </tr>
            <tr>
              <td><strong>{{ $t('firmwares.created') }}:</strong></td>
              <td>{{ $d(new Date(details.createdAt), 'short') }}</td>
            </tr>
            <tr>
              <td><strong>{{ $t('firmwares.lastUpdated') }}:</strong></td>
              <td>{{ $d(new Date(details.updatedAt), 'short') }}</td>
            </tr>
            <tr>
              <td><strong>{{ $t('firmwares.lastAccessed') }}:</strong></td>
              <td>{{ details.accessedAt ? $d(new Date(details.accessedAt), 'short') : '—' }}</td>
            </tr>
            <tr>
              <td><strong>{{ $t('firmwares.downloads') }}:</strong></td>
              <td>{{ details.downloadCount ?? 0 }}</td>
            </tr>
          </tbody>
        </table>
        <div class="buttons">
          <b-button type="is-info" icon-left="download" @click="downloadFirmware">{{ $t('global.download') }}</b-button>
          <b-button type="is-danger" icon-left="delete" @click="confirmRemove">{{ $t('global.delete') }}</b-button>
        </div>
      </div>

      <div class="box">
        <h3 class="title is-5">{{ $t('firmwares.flashOta') }}</h3>
        <div class="content">
          <p>{{ $t('firmwares.flashOtaDesc') }}</p>
        </div>
        <div class="field is-grouped">
          <b-select v-model="selectedDeviceId" :placeholder="$t('firmwares.flashOtaSelect')">
            <option v-for="d in deviceList" :key="d.id" :value="d.id">{{ d.name || d.id }}</option>
          </b-select>
          <div class="control">
            <b-button type="is-danger" icon-left="fire" :loading="flashing" :title="$t('projects.flashTooltip')" @click="flashFirmware">
              {{ $t('projects.flash') }}
            </b-button>
          </div>
        </div>
        <p class="is-size-7 has-text-grey">{{ $t('firmwares.flashOtaHint') }}</p>
      </div>

      <div class="box">
        <h3 class="title is-5">{{ $t('firmwares.flashUsb') }}</h3>
        <div v-if="!supportsWebSerial" class="content">
          <p>{{ $t('firmwares.flashUsbUnsupported') }}</p>
        </div>
        <div v-else class="content">
          <p>{{ $t('firmwares.flashUsbDesc') }}</p>
          <div class="field is-grouped is-align-items-center">
            <div class="control">
              <b-button type="is-link" :loading="preparingManifest" @click="prepareUsbManifest">
                {{ usbManifestUrl ? $t('firmwares.flashUsbReady') : $t('firmwares.flashUsbPrepare') }}
              </b-button>
            </div>
            <div class="control" v-if="usbManifestUrl">
              <!-- eslint-disable-next-line vue/no-undef-components -->
              <esp-web-install-button :manifest="usbManifestUrl"></esp-web-install-button>
            </div>
          </div>
          <p class="is-size-7 has-text-grey">{{ $t('firmwares.flashUsbHint') }}</p>
        </div>
      </div>
    </div>
    <div v-else class="has-text-centered">
      <p class="is-size-4">Not found</p>
      <b-button @click="goToList()" class="mt-4">
        {{ $t('global.back') }}
      </b-button>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, computed, onMounted, ref, getCurrentInstance } from 'vue'
import CopyToClipboard from '@/components/CopyToClipboard.vue'
import { useRoute, useRouter } from 'vue-router'
import { useStore } from 'vuex'
import KeyCloakService from '@/security/KeycloakService'
import { models } from '@/store'
import { formatSize } from '@/utils/formatters'

type FirmwareMeta = {
  sha256: string
  productId?: string
  createdAt?: string
  updatedAt?: string
  accessedAt?: string
  downloadCount?: number
  size?: number
}

type DeviceSummary = { id: string; name?: string }

export default defineComponent({
  name: 'FirmwareDetails',
  components: { CopyToClipboard },
  setup() {
    const route = useRoute()
    const router = useRouter()
    const store = useStore()
    const vm = getCurrentInstance()
    const sha = computed(() => route.params.sha as string)

    const list = computed<FirmwareMeta[]>(() => Array.isArray((store.state as any).firmwares) ? (store.state as any).firmwares : [])
    const details = computed<FirmwareMeta | undefined>(() => list.value.find(f => f.sha256 === sha.value))

    const deviceList = computed<DeviceSummary[]>(() => Object.values(store.state.devices) as DeviceSummary[])
    const selectedDeviceId = ref('')
    const flashing = ref(false)
    const preparingManifest = ref(false)
    const usbManifestUrl = ref<string>('')
    const supportsWebSerial = computed(() => typeof (navigator as any).serial !== 'undefined')

    const load = async () => {
      await Promise.all([
        store.dispatch('loadModel', { model: 'firmwares' }),
        store.dispatch('loadModel', { model: 'devices' }),
      ])
      const first = deviceList.value[0]
      selectedDeviceId.value = first ? first.id : ''
    }

    const goToList = () => router.push({ name: 'firmware-list' })

    const shortSha = (s?: string) => {
      if (!s) return ''
      if (s.length <= 16) return s
      return `${s.slice(0, 8)}…${s.slice(-6)}`
    }

    const openToast = (message: string, type: string) => {
      type ToastProxy = { $buefy?: { toast?: { open?: (opts: { message: string; type: string }) => void } } }
      const proxy = vm?.proxy as unknown as ToastProxy
      proxy?.$buefy?.toast?.open?.({ message, type })
    }

    const confirmRemove = () => {
      type DialogProxy = { $buefy?: { dialog?: { confirm?: (opts: { message: string; type?: string; confirmText?: string; cancelText?: string; hasIcon?: boolean; onConfirm?: () => void }) => void } } }
      const proxy = vm?.proxy as unknown as DialogProxy & { $t?: (key: string, vars?: Record<string, string>) => string }
      const msg = proxy?.$t ? proxy.$t('firmwares.confirmDelete', { sha: sha.value }) : `Delete firmware ${sha.value}?`
      const confirmText = proxy?.$t ? proxy.$t('global.delete') : 'Delete'
      const cancelText = proxy?.$t ? proxy.$t('global.cancel') : 'Cancel'
      proxy?.$buefy?.dialog?.confirm?.({
        message: msg,
        type: 'is-danger',
        confirmText,
        cancelText,
        hasIcon: true,
        onConfirm: async () => {
          try {
            await store.dispatch('deleteFirmware', { sha: sha.value })
            router.push({ name: 'firmware-list' })
          } catch (e) {
            // ignore
          }
        }
      })
    }

    const downloadFirmware = async () => {
      try {
        const resp = await fetch(`${models.firmwares}/${encodeURIComponent(sha.value)}`, {
          headers: { Authorization: `Bearer ${KeyCloakService.Instance.token}` }
        })
        if (!resp.ok) throw new Error(`HTTP ${resp.status}`)
        const blob = await resp.blob()
        const url = window.URL.createObjectURL(blob)
        const a = document.createElement('a')
        a.href = url
        a.download = `firmware-${sha.value}.bin`
        document.body.appendChild(a)
        a.click()
        a.remove()
        window.URL.revokeObjectURL(url)
      } catch (e) {
        // swallow
      }
    }

    const flashFirmware = async () => {
      if (!details.value || !selectedDeviceId.value) {
        const proxy = vm?.proxy as any
        proxy?.$buefy?.toast?.open?.({ message: proxy?.$t('projects.selectDeviceFirst'), type: 'is-warning' })
        return
      }
      try {
        flashing.value = true
        const fwResp = await fetch(`${models.firmwares}/${encodeURIComponent(sha.value)}`, {
          headers: { Authorization: `Bearer ${KeyCloakService.Instance.token}` }
        })
        if (!fwResp.ok) throw new Error(`HTTP ${fwResp.status}`)
        const buf = await fwResp.arrayBuffer()
        const blob = new Blob([buf], { type: 'application/octet-stream' })
        const form = new FormData()
        form.append('file', blob, 'firmware-merged.bin')
        const putUrl = `${models.devices}/${encodeURIComponent(String(selectedDeviceId.value))}/firmware`
        const putResp = await fetch(putUrl, { method: 'PUT', headers: { Authorization: `Bearer ${KeyCloakService.Instance.token}` }, body: form })
        if (!putResp.ok) {
          const txt = await putResp.text().catch(() => '')
          throw new Error(txt || `HTTP ${putResp.status}`)
        }
        const proxy = vm?.proxy as any
        proxy?.$buefy?.toast?.open?.({ message: proxy?.$t('projects.otaTriggered'), type: 'is-success' })
      } catch (e: any) {
        const proxy = vm?.proxy as any
        proxy?.$buefy?.toast?.open?.({ message: `${proxy?.$t('projects.flashFailed')}: ${String(e?.message || e)}`, type: 'is-danger' })
      } finally {
        flashing.value = false
      }
    }

    const createEspWebToolsManifest = async (): Promise<string> => {
      // Build a minimal manifest for esp-web-tools pointing to our firmware blob
      // We will fetch the firmware and serve it via an in-memory blob URL so it carries auth
      const fwResp = await fetch(`${models.firmwares}/${encodeURIComponent(sha.value)}`, {
        headers: { Authorization: `Bearer ${KeyCloakService.Instance.token}` }
      })
      if (!fwResp.ok) throw new Error(`HTTP ${fwResp.status}`)
      const buf = await fwResp.arrayBuffer()
      const binBlob = new Blob([buf], { type: 'application/octet-stream' })
      const binUrl = URL.createObjectURL(binBlob)
      // We use a merged firmware, so no offset is needed
      const manifest = {
        name: `FermiCloud Firmware ${sha.value}`,
        builds: [
          {
            chipFamily: 'ESP32',
            parts: [
              { path: binUrl, offset: 0 }
            ]
          }
        ]
      }
      const manifestBlob = new Blob([JSON.stringify(manifest)], { type: 'application/json' })
      return URL.createObjectURL(manifestBlob)
    }

    const prepareUsbManifest = async () => {
      try {
        preparingManifest.value = true
        usbManifestUrl.value = await createEspWebToolsManifest()
        const proxy = vm?.proxy as any
        proxy?.$buefy?.toast?.open?.({ message: proxy?.$t('firmwares.flashUsbPrepared'), type: 'is-success' })
      } catch (e: any) {
        const proxy = vm?.proxy as any
        proxy?.$buefy?.toast?.open?.({ message: `${proxy?.$t('firmwares.flashUsbPrepareFailed')}: ${String(e?.message || e)}`, type: 'is-danger' })
      } finally {
        preparingManifest.value = false
      }
    }

    onMounted(load)
    return { sha, details, goToList, shortSha, deviceList, selectedDeviceId, flashing, flashFirmware, downloadFirmware, confirmRemove, formatSize, supportsWebSerial, preparingManifest, usbManifestUrl, prepareUsbManifest }
  }
})
</script>

<style scoped>
</style>


